import { useCallback, useMemo } from 'react';

import { VideoPrivacy } from '@vimeo/clip-ui/constants';
import { useLayout } from '@vimeo/clip-ui/hooks/use-layout';
import { translate } from '@vimeo/i18n-runtime';
import { CollectionBpContext } from '@vimeo/use-tracking';
import { useViewer } from '@vimeo/viewer';

import { useAlbumStore } from '../state/albumStore';
import { bpLayoutToggle } from '../utils/analytics';
import { BpCollectionPrivacy } from '../utils/types';
import { useGetBpSessionId } from './use-get-bp-session-id';

export interface LayoutToggleProps {
  clipId: string;
  clipOwnerUri?: string;
  privacyView?: VideoPrivacy;
}

/**
 * Custom hook to manage layout toggle state and behavior.
 * @param clipId - The ID of the clip.
 * @param clipOwnerUri - The URI of the clip owner.
 * @param privacyView - The privacy view setting.
 * @returns The layout toggle state and behavior.
 */
export const useLayoutToggle = ({ clipId, clipOwnerUri, privacyView }: LayoutToggleProps) => {
  const { isSideBySideActive, toggleSideBySideView } = useLayout();
  const { noOfVideos, seoAllowIndexed, showcaseId, showcasePrivacy } = useAlbumStore((state) => ({
    noOfVideos: state.noOfVideos,
    seoAllowIndexed: state.seoAllowIndexed,
    showcaseId: state.albumId,
    showcasePrivacy: state.albumPrivacy,
  }));

  const viewer = useViewer();
  const sessionId = useGetBpSessionId();

  const bpCollectionFields: CollectionBpContext = useMemo(
    () => ({
      entity_id: showcaseId,
      number_of_videos: noOfVideos,
      is_seo_on: seoAllowIndexed,
      collection_privacy: showcasePrivacy as BpCollectionPrivacy,
      collection_embed_privacy: null,
    }),
    [showcasePrivacy, noOfVideos, seoAllowIndexed, showcaseId],
  );

  const onToggleClick = useCallback(() => {
    bpLayoutToggle(
      clipId,
      sessionId,
      bpCollectionFields,
      viewer,
      isSideBySideActive,
      clipOwnerUri,
      privacyView,
    );
    toggleSideBySideView();
  }, [
    bpCollectionFields,
    clipId,
    clipOwnerUri,
    isSideBySideActive,
    privacyView,
    sessionId,
    toggleSideBySideView,
    viewer,
  ]);

  const layoutToggleTooltipText: string = isSideBySideActive
    ? translate('Stacked view')
    : translate('Side by side view');

  const layoutIconTransform = `rotate(${isSideBySideActive ? '0deg' : '90deg'})`;

  return { layoutToggleTooltipText, layoutIconTransform, onToggleClick };
};
